// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { consoleService } from '../../services/consoleService';
import { Subscription, SubscriptionSearchFilters } from '../../types/subscriptions';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import './Subscriptions.scss';

const Subscriptions: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [prevCursor, setPrevCursor] = useState('');

  const userRole = authService.getSessionRole();
  const canViewSubscriptions = userRole === UserRole.USER_ROLE_ADMIN;

  // Get filter values from URL params
  const userId = searchParams.get('user_id') || '';
  const productId = searchParams.get('product_id') || '';
  const store = searchParams.get('store') || '';
  const active = searchParams.get('active');
  const cursor = searchParams.get('cursor') || '';

  useEffect(() => {
    if (canViewSubscriptions) {
      loadSubscriptions();
    } else {
      setLoading(false);
    }
  }, [searchParams, canViewSubscriptions]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError('');

      const filters: SubscriptionSearchFilters = {
        user_id: userId || undefined,
        product_id: productId || undefined,
        store: store || undefined,
        active: active === 'true' ? true : active === 'false' ? false : undefined,
        limit: '100'
      };

      const data = await consoleService.listSubscriptions(filters, cursor);
      setSubscriptions(data.subscriptions || []);
      setNextCursor(data.cursor || '');
      setPrevCursor(data.prev_cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<SubscriptionSearchFilters>) => {
    const params = new URLSearchParams();
    
    if (newFilters.user_id) params.set('user_id', newFilters.user_id);
    if (newFilters.product_id) params.set('product_id', newFilters.product_id);
    if (newFilters.store) params.set('store', newFilters.store);
    if (newFilters.active !== undefined) params.set('active', newFilters.active.toString());
    
    setSearchParams(params);
  };

  const handlePageChange = (newCursor: string) => {
    const params = new URLSearchParams(searchParams);
    if (newCursor) {
      params.set('cursor', newCursor);
    } else {
      params.delete('cursor');
    }
    setSearchParams(params);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStoreIcon = (store: string) => {
    switch (store.toLowerCase()) {
      case 'apple':
      case 'ios':
        return <i className="fab fa-apple text-dark" title="Apple App Store"></i>;
      case 'google':
      case 'android':
        return <i className="fab fa-google-play text-success" title="Google Play Store"></i>;
      default:
        return <i className="fas fa-shopping-cart text-secondary" title={store}></i>;
    }
  };

  const getStatusBadge = (subscription: Subscription) => {
    const now = new Date();
    const expiryDate = new Date(subscription.expiry_time);
    const isExpired = expiryDate < now;
    
    if (subscription.refund_time) {
      return <span className="badge bg-danger">Refunded</span>;
    } else if (!subscription.active) {
      return <span className="badge bg-secondary">Inactive</span>;
    } else if (isExpired) {
      return <span className="badge bg-warning">Expired</span>;
    } else {
      return <span className="badge bg-success">Active</span>;
    }
  };

  if (!canViewSubscriptions) {
    return (
      <div className="subscriptions-container">
        <h2 className="pb-4">Subscriptions</h2>
        <div className="alert alert-warning">
          <strong>Access Denied:</strong> You don't have permission to view subscriptions.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="subscriptions-container">
        <h2 className="pb-4">Subscriptions</h2>
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading subscriptions...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="subscriptions-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Subscriptions</h2>
        <button
          className="btn btn-outline-primary"
          onClick={loadSubscriptions}
          title="Refresh subscriptions"
        >
          <i className="fas fa-sync-alt me-2"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">User ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by user ID..."
                value={userId}
                onChange={(e) => handleFilterChange({ user_id: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Product ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by product ID..."
                value={productId}
                onChange={(e) => handleFilterChange({ product_id: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Store</label>
              <select
                className="form-select"
                value={store}
                onChange={(e) => handleFilterChange({ store: e.target.value })}
              >
                <option value="">All Stores</option>
                <option value="apple">Apple App Store</option>
                <option value="google">Google Play Store</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={active || ''}
                onChange={(e) => handleFilterChange({ active: e.target.value === '' ? undefined : e.target.value === 'true' })}
              >
                <option value="">All Statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Product ID</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Purchase Time</th>
                  <th>Expiry Time</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription, index) => (
                  <tr key={`${subscription.original_transaction_id}-${index}`}>
                    <td className="text-center">
                      {getStoreIcon(subscription.store)}
                    </td>
                    <td>
                      <strong>{subscription.product_id}</strong>
                    </td>
                    <td>
                      <Link
                        to={`/accounts/${subscription.user_id}`}
                        className="text-decoration-none"
                      >
                        <code>{truncateText(subscription.user_id, 15)}</code>
                      </Link>
                    </td>
                    <td>
                      {getStatusBadge(subscription)}
                    </td>
                    <td>{formatDate(subscription.purchase_time)}</td>
                    <td>{formatDate(subscription.expiry_time)}</td>
                    <td>{formatDate(subscription.create_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {subscriptions.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No subscriptions found.</p>
            </div>
          )}

          {/* Pagination */}
          {(nextCursor || prevCursor) && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Showing {subscriptions.length} subscriptions
                </small>
              </div>
              <div className="btn-group">
                {prevCursor && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handlePageChange('')}
                  >
                    First
                  </button>
                )}
                {prevCursor && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handlePageChange(prevCursor)}
                  >
                    Previous
                  </button>
                )}
                {nextCursor && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handlePageChange(nextCursor)}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Statistics */}
      {subscriptions.length > 0 && (
        <div className="row mt-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-primary">{subscriptions.length}</h5>
                <p className="card-text">Total Subscriptions</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-success">
                  {subscriptions.filter(s => s.active && !s.refund_time && new Date(s.expiry_time) > new Date()).length}
                </h5>
                <p className="card-text">Active</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-warning">
                  {subscriptions.filter(s => s.active && !s.refund_time && new Date(s.expiry_time) <= new Date()).length}
                </h5>
                <p className="card-text">Expired</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-danger">
                  {subscriptions.filter(s => s.refund_time).length}
                </h5>
                <p className="card-text">Refunded</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
